<template>
  <div class="flex-container">
    <PageTop
      title="n11 Kataloğundan Ürün Ekle"
      :educationalVideoUrl="'https://magazadestek.n11.com/egitim-detay/n11-katalogundan-nasil-urun-eklenir-20490'"
    />
    
    <SearchBox
      @searchText="searchText = $event"
      @filterSubmit="filterSubmit"
    />
    <EmptyTable v-if="error !== null" errorType="anErrorOccurred" />
    <EmptyTable v-else-if="searchFailedCondition" :didNotFind="didNotFind" />

    <div class="content-area" v-if="isSearchSuccessful || !filtersUpdating">
      <FilterSkeleton v-if="isLoading && filtersUpdating"></FilterSkeleton>
      <Filter v-else :filterItems="filtersItems" @submit="filterSubmit" />
      <SearchResult
        v-if="!(didNotFind || failedSearchValue)"
        :isLoading="isLoading"
        :searchTextForTemplate="searchTextForTemplate"
        :totalResultCount="totalResultCount"
        :table="table"
        :clickHandler="clickHandler"
        :paginatedRows="paginatedRows"
        :loadingClickedProductId="loadingClickedProductId"
        @onSimpleSelectChange="onSimpleSelectChange(Number($event))"
        @isOpen="addCatalogModalRef.isOpen($event)"
      />
      <EmptyTable v-else :didNotFind="didNotFind" class="product-not-found" />
    </div>

  </div>

  <AddCatalogModal
    ref="addCatalogModalRef"
    :paginatedRows="paginatedRows"
    @loadingClickedProductId="loadingClickedProductId = $event"
    @setIsNotificationShow="notification = $event"
    @openListedProductWarningModal="openListedProductWarningModal($event)"
  />

  <ListedProductWarningModal
    ref="listedProductWarningModalRef"
    :pimsId="pimsId"
  />

  <SuccessResultBox
    v-if="notification"
    @closeSuccessResultBox="notification = false"
    :successProcessProductImg="addCatalogModalRef?.successProcessProductImg"
  />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { createColumnHelper, useVueTable, getCoreRowModel } from '@tanstack/vue-table';
import { Filter } from '@n11/so-ui-components';
import useService from './services/getCatalogService';
import FilterSkeleton from '@/components/Skeleton/FilterSkeleton';
import PageTop from '@/components/Page/PageTop.vue';
import { sendProductFilteredSearchEvent } from '@/utils/ga4/helper'; 
import { sendSearchProductEvent } from '@/utils/ga4/helper';
import EmptyTable from './partials/EmptyTable.vue';
import SearchBox from './partials/SearchBox.vue';
import SearchResult from './partials/SearchResult.vue';
import SuccessResultBox from './partials/SuccessResultBox.vue';
import ListedProductWarningModal from './partials/ListedProductWarningModal.vue';
import AddCatalogModal from './partials/AddCatalogModal.vue';

const columnHelper = createColumnHelper();
const paginationOptions = {
  INITIAL_PAGE_INDEX: 0,
  INITIAL_PAGE_SIZE: 21,
  pageSizes: {
    21: '20 Ürün',
    51: '50 Ürün',
    99: '100 Ürün',
  },
  PRODUCT_CARD_SKELETON_COUNT: 21,
};

const loadingClickedProductId = ref(false);
const searchText = ref('');
const table = ref(null);
const filterData = ref({
  "filters": {
    "brands": [],
    "categories": []
  },
});
const pagination = ref({
  pageIndex: paginationOptions.INITIAL_PAGE_INDEX,
  pageSize: paginationOptions.INITIAL_PAGE_SIZE,
});
const {
  data,
  isLoading,
  filtersUpdating,
  pageCount,
  fetchSearchData,
  searchValueSuccess,
  totalResultCount,
  searchTextForTemplate,
  didNotFind,
  failedSearchValue,
  filtersItems,
  error
} = useService(pagination, searchText, filterData);
const addCatalogModalRef = ref();
const listedProductWarningModalRef = ref();
const notification = ref(false);
const showResult = ref(false);
const pimsId = ref();

const isSearchSuccessful = computed(() => error.value === null && hasResults.value && !searchError.value);
const hasResults = computed(() => showResult.value || searchValueSuccess.value);
const searchError = computed(() => didNotFind.value || failedSearchValue.value);
const searchInProgress = computed(() => isSearchSuccessful.value || filtersUpdating.value);
const stillLoading = computed(() => !isSearchSuccessful.value && isLoading.value);
const searchFailedCondition = computed(() => (searchInProgress.value && searchError.value) || stillLoading.value);
const paginatedRows = computed(() => table.value?.getRowModel().rows || []);

const columns = [
  columnHelper.accessor('title', {
    header: 'Ürün Başlığı',
    cell: (info) => info.getValue(),
  }),
];

table.value = useVueTable({
  get data() {
    return data.value ?? [];
  },
  get pageCount() {
    return pageCount.value ?? -1;
  },
  columns,
  manualPagination: true,
  getRowId: row => row.productGroupId,
  getCoreRowModel: getCoreRowModel(),
  onPaginationChange: (updater) => {
    if (typeof updater === 'function') {
      setPagination(
        updater({
          pageIndex: pagination.value.pageIndex,
          pageSize: pagination.value.pageSize,
        }),
      );
    } else {
      setPagination(updater);
    }
  },
  state: {
    pagination: pagination.value,
  },
});

function setPagination({ pageIndex, pageSize }) {
  pagination.value.pageIndex = pageIndex;
  pagination.value.pageSize = pageSize;

  return { pageIndex, pageSize, };
}

const handleEnterPress = (event) => {
  if (event.key === 'Enter') {
    filterSubmit();
  }
};

const clickHandler = async (pageNum) => {
  table.value.setPageIndex(pageNum)
  await fetchSearchData(true)
}

const setFilterData = (selectedItems) => {
  filterData.value.filters = {
    brands: selectedItems.brands?.map(i => i.label) || [],
    categories: selectedItems.categories?.map(i => i.label) || []
  };
};

const filterSubmit = async (selectedItems) => {
  if (selectedItems) {
    setFilterData(selectedItems)
  }
  didNotFind.value = false;
  showResult.value = true;
  table.value.setPageIndex(0);
  await fetchSearchData(true);

  if (filtersUpdating.value) {
    const searchQuery = searchText.value;
    sendSearchProductEvent(searchQuery);
  } else {
    const extractLabel = (items) => {
      return items?.map(item => {
        const label = item.label;
        if (label) {
          const index = label.lastIndexOf('(');
          return index !== -1 ? label.slice(0, index).trim() : label;
        }
        return label || '';
      });
    };

    const categoriesLabels = extractLabel(selectedItems.categories);
    const brandsLabels = extractLabel(selectedItems.brands);

    if ((categoriesLabels && categoriesLabels.length) || (brandsLabels && brandsLabels.length)) {
      sendProductFilteredSearchEvent({
        'kategori_sec': categoriesLabels ? categoriesLabels.join(', ') : '',
        'marka_sec': brandsLabels ? brandsLabels.join(', ') : '',
      });
    }
  }
};

let isSimpleSelectChange = false;
const onSimpleSelectChange = (newSize) => {
  isSimpleSelectChange = true;
  table.value.setPageSize(newSize);
}

const openListedProductWarningModal = (pims_id) => {
  pimsId.value = pims_id;
  listedProductWarningModalRef.value.openListedProductWarningModal();
}

onMounted(() => {
  window.addEventListener('keyup', handleEnterPress);
});

onBeforeUnmount(() => {
  window.removeEventListener('keyup', handleEnterPress);
});

watch(() => pagination.value.pageSize, (newSize, oldSize) => {
  if (isSimpleSelectChange && newSize !== oldSize) {
    fetchSearchData(true).then(() => {
      isSimpleSelectChange = false;
    });
  }
});

</script>


<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.flex-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  margin: 16px 0px;
  gap: 16px;
  width: auto;

  #app {
    width: 100%;

    &> :first-child {
      padding-left: 44px;
      background-position: 12px 50%;
      width: 100%;
    }
  }

  .content-area {
    display: flex;
    gap: 24px;

    >div {
      height: fit-content;
    }

    
  }
}

.title {
  color: var(--Neutral---Background-N80, #242424);
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.2px;
}

.table-title {
  color: var(--Neutral---Background-N80, #242424);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.05px;
  padding-bottom: 24px;
}

.filter {
  width: 100%;
  max-width: 262px;
  width: 100%;
  position: sticky;
  top: 0;
  transition: top 0.2s ease-in-out 0s;
}

.filter-area-top {
  margin-bottom: -14px;
  height: auto;
  width: auto;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.filter-area-bottom {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

</style>